// src/Components/modules/patients/Form/MedicalInfo.js

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Form, Select } from "antd";
import {
  HeartOutlined,
  MedicineBoxOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";

const MedicalInfo = ({
  form,
  clientes,
  regimenes,
  afiliaciones,
  diagnosticos,
  tipos,
  estados,
  regimenId,
}) => {
  // Observa el valor de "tipo_id" en el formulario
  const tipoId = Form.useWatch("tipo_id", form);

  // Encuentra el objeto que tiene nombre "Particular"
  const tipoParticular = tipos?.find((t) => t.nombre === "Particular");
  const isParticular = tipoId === tipoParticular?.id;

  // Encuentra el ID del régimen Subsidiado
  const subsidiadoRegimenId = regimenes.find(
    (reg) => reg.nombre === "Subsidiado"
  )?.id;

  // Obtener el valor actual del régimen del formulario
  const currentRegimenId = form.getFieldValue("regimen_id");
  
  // Usar el regimenId del prop si está disponible, sino usar el valor del formulario
  const effectiveRegimenId = regimenId || currentRegimenId;

  // Filtra afiliaciones basándose en el ID del régimen seleccionado
  const filteredAfiliaciones =
    effectiveRegimenId === subsidiadoRegimenId
      ? afiliaciones.filter((af) => af.nombre === "Beneficiario")
      : afiliaciones;

  // Efecto para manejar cambios en el régimen y actualizar afiliación si es necesario
  useEffect(() => {
    if (effectiveRegimenId === subsidiadoRegimenId) {
      // Si el régimen es Subsidiado, verificar si la afiliación actual es válida
      const currentAfiliacionId = form.getFieldValue("afiliacion_id");
      const isBeneficiario = afiliaciones.some(
        (af) => af.id === currentAfiliacionId && af.nombre === "Beneficiario"
      );
      
      if (!isBeneficiario && currentAfiliacionId) {
        // Si la afiliación actual no es "Beneficiario", limpiar el campo
        form.setFieldsValue({ afiliacion_id: null });
      }
    }
  }, [effectiveRegimenId, subsidiadoRegimenId, afiliaciones, form]);

  // Efecto para asegurar que los valores iniciales se establezcan correctamente
  useEffect(() => {
    // Verificar si hay valores iniciales y si las afiliaciones están cargadas
    if (afiliaciones.length > 0) {
      const currentRegimenId = form.getFieldValue("regimen_id");
      const currentAfiliacionId = form.getFieldValue("afiliacion_id");
      
      // Si hay un régimen y afiliación establecidos, verificar que sean válidos
      if (currentRegimenId && currentAfiliacionId) {
        const isSubsidiado = currentRegimenId === subsidiadoRegimenId;
        const isBeneficiario = afiliaciones.some(
          (af) => af.id === currentAfiliacionId && af.nombre === "Beneficiario"
        );
        
        // Si es subsidiado pero la afiliación no es beneficiario, limpiar
        if (isSubsidiado && !isBeneficiario) {
          form.setFieldsValue({ afiliacion_id: null });
        }
      }
    }
  }, [afiliaciones, form, subsidiadoRegimenId]);

  // Efecto para forzar la actualización de valores cuando el formulario se inicializa
  useEffect(() => {
    // Esperar un poco para que el formulario se establezca completamente
    const timer = setTimeout(() => {
      const currentRegimenId = form.getFieldValue("regimen_id");
      const currentAfiliacionId = form.getFieldValue("afiliacion_id");
      
      // Solo establecer afiliación si no hay una y hay un régimen
      if (currentRegimenId && !currentAfiliacionId && afiliaciones.length > 0) {
        // Buscar la afiliación que corresponde al régimen
        const isSubsidiado = currentRegimenId === subsidiadoRegimenId;
        const targetAfiliacion = isSubsidiado 
          ? afiliaciones.find(af => af.nombre === "Beneficiario")
          : afiliaciones[0]; // O la primera disponible
        
        if (targetAfiliacion) {
          form.setFieldsValue({ afiliacion_id: targetAfiliacion.id });
        }
      }
    }, 200);
    
    return () => clearTimeout(timer);
  }, [afiliaciones, form, subsidiadoRegimenId]);

  return (
    <>
      {/* EPS (seguirá siendo obligatorio en este ejemplo) */}
      <Form.Item
        label="EPS"
        name="eps"
        rules={[{ required: true, message: "Por favor selecciona una EPS." }]}
      >
        <Select
          showSearch
          placeholder="Selecciona EPS"
          allowClear
          optionFilterProp="children"
          filterOption={(input, option) =>
            option?.children.toLowerCase().includes(input.toLowerCase())
          }
          suffixIcon={<MedicineBoxOutlined />}
        >
          {clientes.map((cliente) => (
            <Select.Option key={cliente.id} value={cliente.id}>
              {cliente.nombre_cliente}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {/* Régimen (NO es obligatorio si es Particular) */}
      <Form.Item
        label="Régimen"
        name="regimen_id"
        rules={[
          {
            required: !isParticular,
            message: "Por favor selecciona un régimen.",
          },
        ]}
      >
        <Select
          placeholder="Selecciona régimen"
          allowClear
          suffixIcon={<HeartOutlined />}
        >
          {regimenes.map((reg) => (
            <Select.Option key={reg.id} value={reg.id}>
              {reg.nombre}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {/* Afiliación (NO es obligatorio si es Particular) */}
      <Form.Item
        label="Afiliación"
        name="afiliacion_id"
        rules={[
          {
            required: !isParticular,
            message: "Por favor selecciona una afiliación.",
          },
        ]}
      >
        <Select
          placeholder="Selecciona afiliación"
          allowClear
          suffixIcon={<UserSwitchOutlined />}
        >
          {filteredAfiliaciones.map((af) => (
            <Select.Option key={af.id} value={af.id}>
              {af.nombre}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {/* Diagnóstico (NO es obligatorio si es Particular) */}
      <Form.Item
        label="Diagnóstico"
        name="diagnostico_id"
        rules={[
          {
            required: !isParticular,
            message: "Por favor selecciona un diagnóstico.",
          },
        ]}
      >
        <Select
          showSearch
          placeholder="Selecciona diagnóstico"
          allowClear
          optionFilterProp="children"
          filterOption={(input, option) =>
            option?.children.toLowerCase().includes(input.toLowerCase())
          }
          suffixIcon={<MedicineBoxOutlined />}
        >
          {diagnosticos.map((diag) => (
            <Select.Option key={diag.id} value={diag.id}>
              {diag.nombre}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {/* Tipo (Siempre requerido para que el usuario pueda elegir "Particular") */}
      <Form.Item
        label="Tipo"
        name="tipo_id"
        rules={[{ required: true, message: "Por favor selecciona un tipo." }]}
      >
        <Select
          placeholder="Selecciona tipo"
          allowClear
          suffixIcon={<UserSwitchOutlined />}
        >
          {tipos.map((tipoItem) => (
            <Select.Option key={tipoItem.id} value={tipoItem.id}>
              {tipoItem.nombre}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {/* Estado (asumimos sigue siendo obligatorio) */}
      <Form.Item
        label="Estado"
        name="estado_id"
        rules={[{ required: true, message: "Por favor selecciona un estado." }]}
      >
        <Select
          placeholder="Selecciona estado"
          allowClear
          suffixIcon={<HeartOutlined />}
        >
          {estados.map((estadoItem) => (
            <Select.Option key={estadoItem.id} value={estadoItem.id}>
              {estadoItem.descripcion}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    </>
  );
};

MedicalInfo.propTypes = {
  form: PropTypes.object.isRequired, // Agregamos 'form' como obligatorio
  clientes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      nombre_cliente: PropTypes.string.isRequired,
    })
  ).isRequired,
  regimenes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
  afiliaciones: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
  diagnosticos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
  tipos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
  estados: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      descripcion: PropTypes.string.isRequired,
    })
  ).isRequired,
  regimenId: PropTypes.number,
};

export default MedicalInfo;
